<template>
  <div class="hist">
    <div class="hist-header">
      <h5 class="hist-title">Histórico de versões SQL</h5>
      <p class="hist-hint">Cada alteração na query SQL é registrada com data e autor. Use o modo "Diff" para comparar versões.</p>
      <div class="hist-modes" v-if="items.length > 1">
        <button type="button" class="hist-mode" :class="{ 'is-active': modo === 'lista' }" @click="modo = 'lista'">
          <fa-icon icon="list" /> Lista
        </button>
        <button type="button" class="hist-mode" :class="{ 'is-active': modo === 'diff' }" @click="abrirDiff">
          <fa-icon icon="exchange-alt" /> Diff
        </button>
      </div>
    </div>

    <div v-if="loading" class="hist-loading">
      <fa-icon icon="spinner" spin /> Carregando histórico...
    </div>

    <div v-else-if="!items.length" class="hist-empty">
      <fa-icon icon="history" class="hist-empty__icon" />
      <p class="hist-empty__title">Nenhuma versão registrada</p>
      <p class="hist-empty__hint">Quando o SQL for alterado, as versões anteriores ficarão disponíveis aqui.</p>
    </div>

    <div v-else-if="modo === 'diff'" class="hist-diff">
      <div class="hist-diff__pickers">
        <div class="hist-diff__picker">
          <label>Versão A</label>
          <select v-model.number="diffA" class="form-control">
            <option v-for="h in items" :key="`a-${h.id}`" :value="h.id">#{{ h.id }} — {{ formatarData(h.created_at) }}</option>
          </select>
        </div>
        <div class="hist-diff__picker">
          <label>Versão B</label>
          <select v-model.number="diffB" class="form-control">
            <option v-for="h in items" :key="`b-${h.id}`" :value="h.id">#{{ h.id }} — {{ formatarData(h.created_at) }}</option>
          </select>
        </div>
      </div>
      <div class="hist-diff__panes" v-if="diffData.a && diffData.b">
        <div class="hist-diff__pane">
          <div class="hist-diff__pane-head">A · #{{ diffData.a.id }}</div>
          <pre class="hist-diff__sql"><span
            v-for="(line, i) in diffLines.a"
            :key="`la-${i}`"
            class="hist-diff__line"
            :class="line.cls"
          >{{ line.text + '\n' }}</span></pre>
        </div>
        <div class="hist-diff__pane">
          <div class="hist-diff__pane-head">B · #{{ diffData.b.id }}</div>
          <pre class="hist-diff__sql"><span
            v-for="(line, i) in diffLines.b"
            :key="`lb-${i}`"
            class="hist-diff__line"
            :class="line.cls"
          >{{ line.text + '\n' }}</span></pre>
        </div>
      </div>
    </div>

    <div v-else class="hist-list">
      <div v-for="(h, idx) in items" :key="h.id" class="hist-item">
        <div class="hist-item__rail">
          <div class="hist-item__dot" :class="{ 'is-latest': idx === 0 }"></div>
          <div v-if="idx < items.length - 1" class="hist-item__line"></div>
        </div>
        <div class="hist-item__body">
          <div class="hist-item__meta">
            <span class="hist-item__id">#{{ h.id }}</span>
            <span v-if="idx === 0" class="hist-item__tag">atual</span>
            <span class="hist-item__date"><fa-icon icon="clock" /> {{ formatarData(h.created_at) }}</span>
            <span class="hist-item__author"><fa-icon icon="user" /> {{ h.changed_by_nome || h.changed_by || '—' }}</span>
            <button v-if="onRestore" type="button" class="hist-item__restore" @click="$emit('restore', h)" title="Restaurar este SQL">
              <fa-icon icon="undo" /> Restaurar
            </button>
          </div>
          <pre class="hist-item__sql">{{ h.condition_sql }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';

export default {
  name: 'AcaoObjetoHistoricoTab',
  props: {
    endpointBase: { type: String, required: true },
    acaoId: { type: [Number, String], default: null },
    onRestore: { type: Boolean, default: true },
  },
  data() {
    return { items: [], loading: false, modo: 'lista', diffA: null, diffB: null };
  },
  computed: {
    diffData() {
      const a = this.items.find(i => i.id === this.diffA);
      const b = this.items.find(i => i.id === this.diffB);
      return { a, b };
    },
    diffLines() {
      const a = this.diffData.a ? (this.diffData.a.condition_sql || '').split('\n') : [];
      const b = this.diffData.b ? (this.diffData.b.condition_sql || '').split('\n') : [];
      const setA = new Set(a);
      const setB = new Set(b);
      return {
        a: a.map(t => ({ text: t, cls: setB.has(t) ? 'is-same' : 'is-removed' })),
        b: b.map(t => ({ text: t, cls: setA.has(t) ? 'is-same' : 'is-added' })),
      };
    },
  },
  watch: {
    acaoId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    load() {
      if (!this.acaoId) { this.items = []; return; }
      this.loading = true;
      get(`${this.endpointBase}/${this.acaoId}/historico-condicao`)
        .then(json => {
          this.items = json.data || json.itens || json;
          this.loading = false;
          if (this.items.length >= 2) {
            this.diffA = this.items[1].id;
            this.diffB = this.items[0].id;
          } else if (this.items.length === 1) {
            this.diffA = this.items[0].id;
            this.diffB = this.items[0].id;
          }
        })
        .catch(() => { this.loading = false; });
    },
    abrirDiff() {
      if (this.items.length < 2) return;
      this.modo = 'diff';
    },
    formatarData(s) {
      if (!s) return '—';
      try { return new Date(s).toLocaleString('pt-BR'); } catch (_) { return s; }
    },
  },
};
</script>

<style scoped>
.hist-header { margin-bottom: 16px; display: flex; flex-direction: column; gap: 8px; }
.hist-title { margin: 0; font-size: 13px; font-weight: 700; color: #1d2939; text-transform: uppercase; letter-spacing: 0.4px; }
.hist-hint { margin: 2px 0 0; font-size: 12px; color: #667085; }
.hist-modes { display: inline-flex; align-items: center; gap: 4px; background: #fff; border: 1px solid #dddce9; border-radius: 6px; padding: 2px; width: fit-content; }
.hist-mode {
  background: transparent;
  border: none;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #475467;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
}
.hist-mode.is-active { background: #3a3e81; color: #fff; }

.hist-loading, .hist-empty { text-align: center; padding: 32px; color: #98a2b3; }
.hist-empty__icon { font-size: 32px; color: #d0d5dd; margin-bottom: 12px; opacity: 0.6; }
.hist-empty__title { font-size: 13px; font-weight: 600; color: #475467; margin: 6px 0 2px; }
.hist-empty__hint { font-size: 12px; color: #98a2b3; margin: 0; }

.hist-list { padding-left: 4px; }
.hist-item { display: flex; gap: 14px; }
.hist-item__rail { position: relative; width: 14px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; padding-top: 8px; }
.hist-item__dot { width: 12px; height: 12px; border-radius: 50%; background: #d0d5dd; border: 2px solid #fff; box-shadow: 0 0 0 2px #d0d5dd; z-index: 1; }
.hist-item__dot.is-latest { background: #3a3e81; box-shadow: 0 0 0 2px #3a3e81, 0 0 0 6px rgba(58, 62, 129, 0.18); }
.hist-item__line { flex: 1; width: 2px; background: #e4e7eb; margin-top: 4px; }
.hist-item__body { flex: 1; background: #fff; border: 1px solid #e4e7eb; border-radius: 10px; padding: 12px 14px; margin-bottom: 12px; }
.hist-item__meta { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 11px; color: #667085; margin-bottom: 10px; }
.hist-item__id { background: #f2f4f7; color: #475467; padding: 2px 8px; border-radius: 4px; font-weight: 700; font-size: 11px; }
.hist-item__tag { background: #ecfaee; color: #2e7d32; border: 1px solid #b9e7be; padding: 1px 7px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.hist-item__date, .hist-item__author { display: inline-flex; align-items: center; gap: 5px; }
.hist-item__restore {
  margin-left: auto;
  background: #fff;
  border: 1px solid #dddce9;
  color: #3a3e81;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.hist-item__restore:hover { background: #3a3e81; color: #fff; border-color: #3a3e81; }
.hist-item__sql {
  background: #fafbfc;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
  padding: 10px 12px;
  margin: 0;
  max-height: 180px;
  overflow: auto;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  color: #1d2939;
  line-height: 1.5;
  white-space: pre-wrap;
}

.hist-diff__pickers { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
.hist-diff__picker label { display: block; font-size: 10px; font-weight: 600; color: #98a2b3; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.hist-diff__panes { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.hist-diff__pane { background: #fff; border: 1px solid #e4e7eb; border-radius: 8px; overflow: hidden; }
.hist-diff__pane-head { background: #f9fafb; padding: 8px 12px; font-size: 11px; font-weight: 700; color: #475467; border-bottom: 1px solid #e4e7eb; }
.hist-diff__sql {
  margin: 0;
  padding: 10px 12px;
  font-family: 'Menlo', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.5;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
}
.hist-diff__line { display: block; padding: 0 4px; }
.hist-diff__line.is-removed { background: #fdecea; color: #b3261e; }
.hist-diff__line.is-added { background: #ecfaee; color: #2e7d32; }
.hist-diff__line.is-same { color: #475467; }
</style>
